/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant, type DiscoveredTenant } from './TenantContext';
import { logger } from '../lib/logger';
import { resolveLoginErrorMessage } from '../lib/auth-errors';
import { setSentryUser, clearSentryUser } from '../lib/sentry';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarUrl?: string;
  roles?: string[];
  permissions?: string[];
}

interface LoginResult {
  ok: boolean;
  /** problem+json kodundan çözülen TR mesajı; yoksa çağıran generic fallback gösterir. */
  errorMessage?: string;
}

interface AuthContextType {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  discoverTenant: (email: string) => Promise<DiscoveredTenant | null>;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  hasPermission: (code: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { tenantSlug, setTenantFromDiscovery } = useTenant();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const staleChecked = useRef(false);

  useEffect(() => {
    // Check for existing session on mount
    const storedToken = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- session restoration from localStorage on mount
      setAccessToken(storedToken);
      setUser(JSON.parse(storedUser));
    }

    setIsLoading(false);
  }, []);

  const getHeaders = () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (tenantSlug) {
      headers['X-Tenant-Slug'] = tenantSlug;
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return headers;
  };

  const discoverTenant = async (email: string): Promise<DiscoveredTenant | null> => {
    try {
      const response = await fetch(`${API_URL}/api/auth/discover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        return null;
      }

      const data: DiscoveredTenant = await response.json();

      // Update tenant context with discovered tenant
      setTenantFromDiscovery(data);

      return data;
    } catch (error) {
      logger.error('Tenant discovery error', error);
      return null;
    }
  };

  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        return { ok: false, errorMessage: resolveLoginErrorMessage(body) };
      }

      const data = await response.json();

      setAccessToken(data.accessToken);
      setUser(data.user);

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      // Plan PII kuralı: email/name yollamıyoruz; sadece id + role + tenantSlug.
      setSentryUser({
        id: data.user.id,
        role: data.user.roles?.[0],
        tenantSlug: tenantSlug ?? undefined,
      });

      return { ok: true };
    } catch (error) {
      logger.error('Login error', error);
      return { ok: false };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken && accessToken) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch (error) {
      logger.error('Logout error', error);
    } finally {
      setUser(null);
      setAccessToken(null);
      clearSentryUser();

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');

      // Drop every cached query so a same-tab user switch (logout → login without
      // a full reload) cannot briefly surface the previous user's data.
      queryClient.clear();
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (!storedRefreshToken) {
        return false;
      }

      const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        await logout();
        return false;
      }

      const data = await response.json();

      setAccessToken(data.accessToken);
      setUser(data.user);

      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      return true;
    } catch (error) {
      logger.error('Token refresh error', error);
      await logout();
      return false;
    }
  };

  useEffect(() => {
    if (isLoading || staleChecked.current) return;
    staleChecked.current = true;
    if (user && user.permissions === undefined) {
      // Stale session from before conditional-UI shipped — repopulate permissions.
      // eslint-disable-next-line react-hooks/set-state-in-effect -- refreshToken setState's only after an awaited fetch
      void refreshToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot after load; refreshToken is recreated each render
  }, [isLoading, user]);

  const hasPermission = (code: string): boolean =>
    user?.permissions?.includes(code) ?? false;

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isAuthenticated: !!user && !!accessToken,
        isLoading,
        discoverTenant,
        login,
        logout,
        refreshToken,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
